import { busy, fmtDate, h, mount, toast } from '../dom.ts';
import type { InstallMailReport, PushResult } from '../api.ts';
import { inPushScope, installSender, installTargets, PUSH_SCOPE_LABEL } from '../../../supabase/functions/_shared/core/index.ts';

import { reload, type Store } from '../state.ts';

/** Qui a déclenché une exécution : « api » = appel par clé (aucun cron n'existe) ; « cron » = ancien libellé du même appel. */
const CALLER_LABEL: Record<string, string> = { api: 'Appel API', cron: 'Appel API' };

const ACTION_LABEL: Record<PushResult['action'], string> = {
  pushed: 'Poussée',
  would_push: 'Serait poussée',
  unchanged: 'Inchangée',
  skipped: 'Ignorée',
  error: 'Erreur',
  too_long: 'Trop longue',
};

const STAT_LABEL: Record<string, string> = {
  would_send: 'Recevraient le mail',
  sent: 'Envoyés',
  directory: 'Annuaire',
  people: 'Personnes concernées',
  fields: 'Champs',
  applied: 'Reprises',
  created: 'Créés',
  known: 'Déjà connues (non modifiées)',
  updated: 'Mis à jour', // anciennes exécutions (avant le 07/10) : comptait en fait les adresses déjà connues
  deactivated: 'Désactivés',
  suspended: 'Passées en suspendues',
  restored: 'Suspensions levées',
  reactivated: 'Réactivées',
  errors: 'Erreurs',
};

export function deployView(store: Store, refresh: () => void): HTMLElement {
  const root = h('div', { class: 'stack' });
  const results = h('div', { class: 'panel' });
  const history = h('div', { class: 'panel' });

  const showPush = (
    title: string,
    data: { stats: Record<string, number>; results: PushResult[] },
  ) => {
    // Deux choses que l'on ne mélange plus : les ERREURS (une boîte qui ne sera pas poussée) et les
    // AVERTISSEMENTS de mise en page (corrections automatiques au rendu), repliés et comptés.
    const failures = data.results.filter((r) => r.action === 'error' || r.action === 'too_long');
    const warnings = data.results.flatMap((r) => (r.warnings ?? []).map((w) => ({ who: r.sendAs ?? r.person, w })));
    const grouped = [...warnings.reduce((m, { who, w }) => m.set(w, [...(m.get(w) ?? []), who]), new Map<string, string[]>())];
    mount(
      results,
      h('h3', null, title),
      failures.length
        ? h(
          'div',
          { class: 'push-errors' },
          h('h4', null, `${failures.length} boîte${failures.length > 1 ? 's' : ''} non poussée${failures.length > 1 ? 's' : ''}`),
          h('ul', null, failures.map((r) => h('li', null, h('strong', null, r.sendAs ?? r.person), ` — ${ACTION_LABEL[r.action]} : ${r.error ?? (r.length ? `${r.length} caractères` : '')}`))),
        )
        : null,
      h(
        'div',
        { class: 'meta' },
        Object.entries(data.stats).map(([k, v]) =>
          h(
            'span',
            { class: `chip ${k}` },
            `${ACTION_LABEL[k as PushResult['action']] ?? k} : ${v}`,
          )
        ),
      ),
      h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          null,
          h(
            'thead',
            null,
            h(
              'tr',
              null,
              ['Adresse', 'Résultat', 'Campagne', 'Détail'].map((t) => h('th', null, t)),
            ),
          ),
          h(
            'tbody',
            null,
            data.results.map((r) =>
              h(
                'tr',
                { class: r.action },
                h('td', null, r.sendAs ?? r.person),
                h('td', null, ACTION_LABEL[r.action]),
                h('td', null, r.campaign ?? '—'),
                h(
                  'td',
                  null,
                  r.error ?? (r.length ? `${r.length} car.${r.warnings?.length ? ` · ${r.warnings.length} avertissement${r.warnings.length > 1 ? 's' : ''}` : ''}` : ''),
                ),
              )
            ),
          ),
        ),
      ),
      warnings.length
        ? h(
          'details',
          { class: 'push-warnings' },
          h('summary', null, `${warnings.length} avertissement${warnings.length > 1 ? 's' : ''} de mise en page — déplier`),
          h('p', { class: 'muted' }, 'Corrections faites automatiquement au rendu (filet de sécurité). Elles signalent une donnée de charte à mettre d’équerre : ouvrir la mise en page concernée puis « Terminer ».'),
          h('ul', null, grouped.map(([w, who]) => h('li', null, `${w} — ${who.length} boîte${who.length > 1 ? 's' : ''}`, h('small', { class: 'muted' }, ` (${who.slice(0, 4).join(', ')}${who.length > 4 ? '…' : ''})`)))),
        )
        : null,
    );
  };

  const drawHistory = async () => {
    const [runs, states] = await Promise.all([
      store.api.listRuns(),
      store.api.listStates(),
    ]);
    const errors = states.filter((s) => s.last_error);
    mount(
      history,
      h('h3', null, 'Historique'),
      runs.length
        ? h(
          'div',
          { class: 'table-wrap' },
          h(
            'table',
            null,
            h(
              'thead',
              null,
              h(
                'tr',
                null,
                ['Date', 'Type', 'Par', 'Résultat'].map((t) => h('th', null, t)),
              ),
            ),
            h(
              'tbody',
              null,
              runs.map((r) =>
                h(
                  'tr',
                  null,
                  h('td', null, fmtDate(r.started_at)),
                  h(
                    'td',
                    null,
                    `${r.kind === 'sync' ? 'Annuaire' : r.kind === 'resync' ? 'Reprise depuis Google' : r.kind === 'install_mail' ? 'Liens d’installation' : 'Signatures'}${r.dry_run ? ' (simulation)' : ''}`,
                  ),
                  h('td', null, r.triggered_by ? CALLER_LABEL[r.triggered_by] ?? r.triggered_by : '—', r.kind === 'install_mail' && r.sender ? h('small', { class: 'muted' }, `expéditeur : ${r.sender}`) : null),
                  h(
                    'td',
                    null,
                    r.error ?? (r.stats
                      ? Object.entries(r.stats).map(([k, v]) =>
                        `${
                          ACTION_LABEL[k as PushResult['action']] ??
                            STAT_LABEL[k] ?? k
                        } : ${v}`
                      ).join(' · ')
                      : 'en cours…'),
                  ),
                )
              ),
            ),
          ),
        )
        : h('p', { class: 'muted' }, 'Aucune exécution.'),
      errors.length
        ? [
          h('h3', null, `Adresses en erreur (${errors.length})`),
          h(
            'ul',
            { class: 'warnings' },
            errors.map((s) => h('li', null, `${s.send_as_email} — ${s.last_error}`)),
          ),
        ]
        : null,
    );
  };

  const only = h('input', {
    type: 'email',
    placeholder: 'adresse principale à tester',
  });
  const btn = (
    label: string,
    cls: string,
    fn: (b: HTMLButtonElement) => Promise<void>,
  ) => {
    const b = h('button', { class: cls }, label);
    b.addEventListener('click', () => busy(b, () => fn(b)));
    return b;
  };
  /** Un bouton et, dessous, la phrase qui dit quand s'en servir. */
  const action = (button: HTMLElement, why: string) => h('div', { class: 'action' }, button, h('small', { class: 'muted' }, why));
  /** Boîtes que « Forcer tout » réécrira : LE prédicat du handler (core/scope.ts), compté à chaque appel. */
  const pushScope = () => store.people.filter(inPushScope).length;

  /** Résultat d'une passe d'annuaire en clair (jamais de JSON brut) ; rouge avec le détail s'il y a eu des erreurs. */
  const syncToast = (r: { stats: Record<string, number>; errors?: { email: string; error: string }[] }, dryRun: boolean) => {
    const s = r.stats;
    const n = (v: number | undefined) => v ?? 0;
    const pl = (v: number, one: string, many = `${one}s`) => `${v} ${v > 1 ? many : one}`;
    const head = dryRun ? 'Simulation' : 'Annuaire synchronisé';
    // La synchro n'écrase ni prénom, ni nom, ni fonction, ni téléphone — mais elle ÉCRIT : elle crée, désactive,
    // suit la suspension (une boîte suspendue sort du périmètre de poussée) et réactive. On le dit, chiffré.
    const effects = [
      n(s.suspended) ? `${n(s.suspended)} ${dryRun ? 'passerai' : 'passé'}${n(s.suspended) > 1 ? 'ent' : 't'} en suspendue (hors périmètre de poussée)` : '',
      n(s.restored) ? `${n(s.restored)} suspension${n(s.restored) > 1 ? 's' : ''} levée${n(s.restored) > 1 ? 's' : ''} (de retour dans le périmètre)` : '',
      n(s.reactivated) ? `${n(s.reactivated)} réactivée${n(s.reactivated) > 1 ? 's' : ''} (inactive${n(s.reactivated) > 1 ? 's' : ''} ici, toujours dans Google)` : '',
    ].filter(Boolean);
    const body = `${pl(n(s.directory), 'adresse')} dans l’annuaire — ${n(s.created)} ${dryRun ? 'à créer' : pl(n(s.created), 'créée').split(' ')[1]}, ${n(s.known)} déjà ${n(s.known) > 1 ? 'connues' : 'connue'} (prénom, nom, fonction, téléphone intacts ; lien Google, suspension et activité rafraîchis), ${n(s.deactivated)} ${dryRun ? 'à désactiver' : pl(n(s.deactivated), 'désactivée').split(' ')[1]}${effects.length ? ' ; ' + effects.join(', ') : ''}.`;
    const errs = r.errors ?? [];
    if (n(s.errors) > 0 || errs.length) {
      toast(`${head} : ${body} ${pl(n(s.errors) || errs.length, 'erreur')} : ${errs.map((e) => `${e.email} (${e.error})`).join(' ; ') || 'voir l’historique'}`, 'error');
    } else toast(`${head} : ${body} Aucune erreur.`);
  };

  const autoBox = h('section', { class: 'panel' });
  const drawAuto = async () => {
    const st = await store.api.automationStatus();
    const on = st.auto_push;
    // Le texte dit l'état RÉEL : l'interrupteur, et l'existence ou non d'une tâche planifiée.
    const planned = st.pg_cron && st.cron_jobs > 0 ? `${st.cron_jobs} tâche(s) planifiée(s) en base` : 'aucune tâche planifiée (pg_cron non installé)';
    mount(
      autoBox,
      h(
        'div',
        { class: 'row between' },
        h(
          'div',
          null,
          h(
            'h3',
            null,
            `Poussée automatique : ${on ? 'ACTIVÉE' : 'désactivée'}`,
          ),
          h(
            'p',
            { class: 'muted' },
            on
              ? `Un appel API par clé pousse les changements ; ${planned}. Rien ne part sans un appel ou un clic ici.`
              : `Rien ne part vers Gmail sans action manuelle (${planned}). À activer seulement après le test sur une adresse ET la coupure de Signitic.`,
          ),
        ),
        btn(
          on ? 'Désactiver' : 'Activer',
          on ? 'ghost danger' : '',
          async () => {
            if (
              !on &&
              !confirm(
                'Activer la poussée automatique pour TOUTES les boîtes gérées ? Vérifiez que Signitic est coupé.',
              )
            ) return;
            await store.api.setAutoPush(!on);
            await drawAuto();
          },
        ),
      ),
    );
  };

  // Envoi des liens d'installation (Apple Mail / Outlook) : ciblage par le marquage « local_client »
  // de la fiche Personne, simulation nominative, test sur soi, confirmation explicite, tracé dans runs.
  // Jamais par cron ni par auto_push : la fonction refuse tout appel par clé.
  const mailBox = h('section', { class: 'panel' });
  const mailResults = h('div');
  const showMail = (title: string, r: InstallMailReport) => {
    mount(
      mailResults,
      h('h4', null, title),
      r.sender ? h('p', null, h('strong', null, 'Expéditeur : '), `${r.sender.name} <${r.sender.email}>`, ' — les réponses arrivent à cette adresse.') : null,
      h('div', { class: 'meta' }, Object.entries(r.stats).map(([k, v]) => h('span', { class: `chip ${k}` }, `${STAT_LABEL[k] ?? k} : ${v}`))),
      r.results.length
        ? h(
          'div',
          { class: 'table-wrap' },
          h(
            'table',
            null,
            h('thead', null, h('tr', null, ['Destinataire', 'Nom', 'Résultat', 'Lien / détail'].map((t) => h('th', null, t)))),
            h('tbody', null, r.results.map((x) => h('tr', { class: x.action === 'error' ? 'error' : '' }, h('td', null, x.person), h('td', null, x.name ?? '—'), h('td', null, STAT_LABEL[x.action] ?? x.action), h('td', null, x.error ?? x.link ?? x.messageId ?? '')))),
          ),
        )
        : h('p', { class: 'muted' }, 'Personne : aucune boîte gérée et active n’est marquée « Apple Mail / Outlook » (fiche Personne).'),
      r.sample ? h('details', null, h('summary', null, `Aperçu du mail envoyé (sujet : ${r.sample.subject})`), h('pre', { class: 'mail-preview' }, r.sample.text)) : null,
    );
  };
  const drawMail = () => {
    const targets = installTargets(store.people);
    const me = store.user;
    const who = installSender(store.people, me);
    mount(
      mailBox,
      h('h3', null, '3. Envoyer les liens d’installation (Apple Mail / Outlook)'),
      h('p', { class: 'muted' }, `Envoie à chaque personne marquée « Écrit aussi depuis Apple Mail / Outlook » (fiche Personne) un mail avec son lien personnel. Cibles aujourd’hui : ${targets.length} boîte${targets.length > 1 ? 's' : ''}${targets.length ? ` (${targets.slice(0, 6).map((p) => p.primary_email).join(', ')}${targets.length > 6 ? '…' : ''})` : ''}. Jamais automatique : chaque envoi est un clic ici, tracé dans l’historique.`),
      // L'expéditeur est la personne connectée : elle doit le voir avant de cliquer, et savoir que les réponses lui arrivent.
      who.ok
        ? h('p', { class: 'sender' }, h('strong', null, `Ces messages partiront de ${who.sender.email}`), ` (« ${who.sender.name} »). Les réponses arriveront dans votre boîte.`)
        : h('p', { class: 'warnings' }, who.reason),
      h(
        'div',
        { class: 'row' },
        btn('Simuler (qui recevrait quoi)', 'ghost', async () => {
          showMail('Simulation', await store.api.sendInstallLinks({ dryRun: true }));
          await drawHistory();
        }),
        btn('Envoyer pour de vrai', '', async () => {
          if (!targets.length) throw new Error('Aucune cible : marquez d’abord les personnes concernées dans Personnes.');
          const typed = prompt(`Envoi RÉEL à ${targets.length} boîte${targets.length > 1 ? 's' : ''} marquée${targets.length > 1 ? 's' : ''} Apple Mail / Outlook. Tapez ENVOYER pour confirmer.`);
          if (typed !== 'ENVOYER') return;
          showMail('Envoi réel', await store.api.sendInstallLinks({}));
          await drawHistory();
        }),
      ),
      mailResults,
    );
  };

  // Le compteur d'invocations Cloudflare vit dans l'écran Statistiques (avec les clics).
  const edgeBox = h('section', { class: 'panel' }, h('h3', null, 'Invocations des routes publiques'), h('p', { class: 'muted' }, 'Le compteur des requêtes /a/, /b/, /go/ (quota Cloudflare, alerte à 50 000 par jour) est dans l’onglet ', h('a', { href: '#statistiques' }, 'Statistiques'), '.'));

  mount(
    root,
    store.api.demo
      ? h(
        'div',
        { class: 'banner-demo' },
        'Mode démo : les boutons simulent les appels, rien n’est envoyé à Google.',
      )
      : null,
    autoBox,
    h(
      'div',
      { class: 'cards two' },
      h(
        'section',
        { class: 'panel' },
        h('h3', null, '1. Annuaire Google Workspace'),
        h(
          'p',
          { class: 'muted' },
          'Importe les comptes de tous les domaines (bonjourbonsoir.paris, badaboum.paris, virage17.paris…). Additive uniquement : crée les nouvelles adresses, désactive les disparues, suit les suspensions ; n’écrase jamais prénom, nom, fonction ni téléphone (pour cela : « Reprendre depuis Google », écran Personnes). À la demande : aucune synchronisation planifiée n’existe.',
        ),
        h(
          'div',
          { class: 'row' },
          btn('Simuler', 'ghost', async () => {
            syncToast(await store.api.sync(true), true);
            await drawHistory();
          }),
          btn('Synchroniser', '', async () => {
            syncToast(await store.api.sync(false), false);
            await reload(store);
            await drawHistory();
          }),
        ),
      ),
      h(
        'section',
        { class: 'panel' },
        h('h3', null, '2. Pousser les signatures dans Gmail'),
        h(
          'p',
          { class: 'muted' },
          'À la demande (bouton ou appel API par clé) : aucune poussée planifiée n’existe.',
        ),
        h(
          'div',
          { class: 'actions-explained' },
          action(
            btn('Simuler', 'ghost', async () => {
              showPush('Simulation', await store.api.push({ dryRun: true }));
              await drawHistory();
            }),
            'Calcule ce qui serait poussé et l’affiche. N’écrit rien dans Gmail.',
          ),
          action(
            btn('Pousser les changements', '', async () => {
              showPush('Poussée', await store.api.push({}));
              await drawHistory();
            }),
            'Ne met à jour que les boîtes dont la signature a changé depuis le dernier envoi. Usage courant.',
          ),
          action(
            btn('Forcer tout', 'ghost danger', async () => {
              const n = pushScope(); // recompté au moment de la confirmation
              if (confirm(`Réécrire maintenant, sans comparaison, les ${n} boîtes du périmètre (${PUSH_SCOPE_LABEL}) ?`)) {
                showPush('Poussée forcée', await store.api.push({ force: true }));
                await drawHistory();
              }
            }),
            `Réécrit sans comparaison les ${pushScope()} boîtes du périmètre (${PUSH_SCOPE_LABEL} ; les boîtes suspendues dans Google sont exclues). À utiliser le jour de la bascule Signitic : après la coupure, les boîtes contiennent encore les signatures Signitic alors que l’outil croit les avoir déjà mises à jour — « Pousser les changements » ne ferait rien.`,
          ),
        ),
        h(
          'div',
          { class: 'row' },
          only,
          btn('Tester sur cette adresse', 'ghost', async () => {
            const e = only.value.trim().toLowerCase();
            if (!e) throw new Error('Saisissez une adresse');
            showPush(
              `Test ${e}`,
              await store.api.push({ only: [e], force: true }),
            );
            await drawHistory();
          }),
        ),
      ),
    ),
    mailBox,
    results,
    history,
    edgeBox,
  );
  drawHistory();
  drawAuto();
  drawMail();
  void refresh;
  return root;
}
